import { useState, type FormEvent, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Check, MessageCircle, MapPin } from "lucide-react";
import { useIdentity } from "@/lib/identity/useIdentity";
import {
  requestReservationOtp,
  verifyReservationOtp,
  createProductReservation,
} from "@/lib/reservations.functions";
import { useAutoOpenPopup } from "@/hooks/useAutoOpenPopup";
import { isInternalTraffic } from "@/lib/meta/consent";
import { COMPANY_MARKETING_CHANNELS } from "@/lib/consent/company-marketing";
import { ConsentBlock, defaultConsentSelection } from "@/components/consent/ConsentBlock";

interface Props {
  productHandle: string;
  productName: string;
  trigger: ReactNode;
  source?: string;
}

type Step = "contact" | "otp" | "address" | "done";

const PHONE_RE = /^(?:\+?91[\-\s]?|0)?[6-9]\d{9}$/;
const PIN_RE = /^\d{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PreReserveDialog({ productHandle, productName, trigger, source = "pdp-sold-out" }: Props) {
  const { anonymousId } = useIdentity();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("contact");
  useAutoOpenPopup("prereserve", setOpen);
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  // Opaque server-issued proof that this browser verified this number.
  const [verification, setVerification] = useState<string | null>(null);
  const [code, setCode] = useState("");

  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [reason, setReason] = useState("");
  const [promotionalChannels, setPromotionalChannels] = useState(() => defaultConsentSelection([...COMPANY_MARKETING_CHANNELS]));

  const requestOtp = useServerFn(requestReservationOtp);
  const verifyOtp = useServerFn(verifyReservationOtp);
  const createReservation = useServerFn(createProductReservation);

  const reset = () => {
    setStep("contact");
    setName("");
    setEmail("");
    setPhone("");
    setNormalizedPhone("");
    setCode("");
    setOtpVerified(false);
    setVerification(null);
    setLine1("");
    setLine2("");
    setCity("");
    setState("");
    setPincode("");
    setReason("");
    setPromotionalChannels(defaultConsentSelection([...COMPANY_MARKETING_CHANNELS]));
  };

  const onContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    const cleanedPhone = phone.trim().replace(/\s|-/g, "");
    if (!PHONE_RE.test(cleanedPhone)) return toast.error("Enter a valid 10-digit mobile number");
    if (email.trim() && !EMAIL_RE.test(email.trim())) return toast.error("Enter a valid email or leave it blank");
    if (promotionalChannels.email && !email.trim()) return toast.error("Enter an email address or leave optional email offers unchecked");
    setLoading(true);
    try {
      const res = await requestOtp({ data: { phone: cleanedPhone } });
      if (!res.ok) {
        if (res.error === "rate_limited") {
          toast.error(`Too many requests. Try again in ${Math.ceil((res.retryAfterSec ?? 60) / 60)} min.`);
        } else if (res.error === "invalid_phone") {
          toast.error("Enter a valid Indian mobile number");
        } else {
          toast.error("Couldn't send OTP. Try again.");
        }
        return;
      }
      setNormalizedPhone(res.phone);
      setStep("otp");
      toast.success("OTP sent on WhatsApp");
    } catch {
      toast.error("Couldn't send OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const onOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const res = await verifyOtp({ data: { phone: normalizedPhone, code, anonymousId: anonymousId || null } });
      if (!res.ok) {
        const msg =
          res.error === "invalid" ? "Incorrect code" :
          res.error === "expired" ? "Code expired. Request a new one." :
          res.error === "too_many_attempts" ? "Too many attempts. Request a new code." :
          "No active code. Request a new one.";
        toast.error(msg);
        return;
      }
      setOtpVerified(true);
      setVerification(res.verification ?? null);
      setStep("address");
    } finally {
      setLoading(false);
    }
  };

  const onResendOtp = async () => {
    setLoading(true);
    try {
      const res = await requestOtp({ data: { phone: normalizedPhone || phone } });
      if (res.ok) {
        toast.success("New code sent");
        setCode("");
      } else if (res.error === "rate_limited") {
        toast.error(`Wait ${res.retryAfterSec ?? 45}s before resending`);
      } else {
        toast.error("Couldn't resend");
      }
    } finally {
      setLoading(false);
    }
  };

  const onAddressSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!otpVerified || !normalizedPhone || !verification) {
      toast.error("Please verify your mobile number first");
      setStep("otp");
      return;
    }
    if (line1.trim().length < 3) return toast.error("Enter your address");
    if (!city.trim()) return toast.error("Enter your city");
    if (!state.trim()) return toast.error("Enter your state");
    if (!PIN_RE.test(pincode.trim())) return toast.error("Enter a valid 6-digit pincode");
    if (reason.trim().length > 500) return toast.error("Reason must be 500 characters or less");
    setLoading(true);
    try {
      const res = await createReservation({
        data: {
          productHandle,
          name: name.trim(),
          phone: normalizedPhone,
          email: email.trim() ? email.trim().toLowerCase() : null,
          address: {
            line1: line1.trim(),
            line2: line2.trim() || null,
            city: city.trim(),
            state: state.trim(),
            pincode: pincode.trim(),
          },
          reason: reason.trim() || null,
          anonymousId: anonymousId || null,
          source,
          verification,
          promotionalChannels,
          // Internal/automated traffic is excluded from ad reporting, including
          // on the production host. Never affects the reservation itself.
          internal: isInternalTraffic(),
        },
      });
      if (!res.ok) {
        toast.error(res.error === "unverified" ? "Please verify your mobile again" : "Couldn't save reservation");
        return;
      }
      setStep("done");
    } catch {
      toast.error("Couldn't save reservation");
    } finally {
      setLoading(false);
    }
  };

  const stepIcon =
    step === "done" ? <Check className="h-6 w-6" /> :
    step === "address" ? <MapPin className="h-6 w-6" /> :
    step === "otp" ? <MessageCircle className="h-6 w-6" /> :
    <ShieldCheck className="h-6 w-6" />;

  const stepTitle =
    step === "done" ? "You're reserved" :
    step === "address" ? "Where should we ship?" :
    step === "otp" ? "Verify your mobile" :
    `${productHandle === "ova-balance" ? "Pre-book" : "Pre-reserve"} ${productName}`;

  const stepDesc =
    step === "done"
      ? `We'll ship your ${productName} the day stock lands and send a WhatsApp update.`
      : step === "address"
      ? "We'll use this only to ship your reservation."
      : step === "otp"
      ? `Enter the 6-digit code we sent on WhatsApp to ${normalizedPhone}.`
      : "Free reservation. Priority shipping when stock arrives.";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
            {stepIcon}
          </div>
          <DialogTitle className="text-center font-display text-xl">{stepTitle}</DialogTitle>
          <DialogDescription className="text-center">{stepDesc}</DialogDescription>
        </DialogHeader>

        {step === "contact" && (
          <form onSubmit={onContactSubmit} className="mt-2 space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              maxLength={120}
              required
              autoFocus
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional)"
              maxLength={255}
              inputMode="email"
              autoComplete="email"
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number (WhatsApp)"
              maxLength={20}
              inputMode="tel"
              autoComplete="tel"
              required
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send WhatsApp OTP"}
            </Button>
            <p className="pt-1 text-center text-[11px] text-muted-foreground">
              Free to reserve · No payment now
            </p>
            <label className="flex items-start gap-2.5 text-[12px] leading-snug text-muted-foreground">
              <input
                type="checkbox"
                checked={!!promotionalChannels.whatsapp}
                onChange={(e) => setPromotionalChannels({ ...promotionalChannels, whatsapp: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
              />
              <span>
                Send me occasional early-access offers on WhatsApp (optional).{" "}
                <a href="/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Terms</a>
              </span>
            </label>

          </form>
        )}

        {step === "otp" && (
          <form onSubmit={onOtpSubmit} className="mt-2 space-y-3">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              required
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-center text-lg tracking-[0.4em] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <Button type="submit" disabled={loading || code.length !== 6} className="h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </Button>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <button type="button" onClick={() => setStep("contact")} className="underline underline-offset-2">
                Change number
              </button>
              <button type="button" onClick={onResendOtp} disabled={loading} className="underline underline-offset-2 disabled:opacity-50">
                Resend code
              </button>
            </div>
          </form>
        )}

        {step === "address" && (
          <form onSubmit={onAddressSubmit} className="mt-2 space-y-3">
            <input
              type="text"
              value={line1}
              onChange={(e) => setLine1(e.target.value)}
              placeholder="Address line 1 (house, street)"
              maxLength={200}
              required
              autoFocus
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <input
              type="text"
              value={line2}
              onChange={(e) => setLine2(e.target.value)}
              placeholder="Address line 2 (apartment, landmark)"
              maxLength={200}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                maxLength={80}
                required
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="State"
                maxLength={80}
                required
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>
            <input
              type="text"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Pincode"
              inputMode="numeric"
              maxLength={6}
              required
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <div className="space-y-1">
              <textarea
                value={reason}
                onChange={(e) =>
                  setReason(
                    e.target.value
                      // strip control chars except newline/tab
                      .replace(/[\x00-\x08\x0B-\x1F\x7F]/g, "")
                      .slice(0, 500),
                  )
                }
                placeholder="What made you pre-reserve? (optional)"
                rows={3}
                maxLength={500}
                aria-describedby="reason-counter"
                className="w-full resize-none rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
              <div
                id="reason-counter"
                className={`text-right text-[11px] ${reason.length >= 500 ? "text-destructive" : "text-muted-foreground"}`}
              >
                {reason.length}/500
              </div>
            </div>
            <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm reservation"}
            </Button>
          </form>
        )}

        {step === "done" && (
          <Button
            onClick={() => setOpen(false)}
            className="mt-2 h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90"
          >
            Done
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
